/**
 * EMR Pointer - Highlights and draws ghost paths to UI elements
 * Adapted from PRISM Chrome Extension for React/Spectra demo
 */

class EMRPointer {
  constructor() {
    this.activePath = null;
    this.activeTooltip = null;
    this.activeTarget = null;
    this.activeClickHandler = null;
    this.activeClickTarget = null;
    this.initStyles();
  }

  initStyles() {
    if (document.getElementById('prism-emr-pointer-styles')) return;
    
    const style = document.createElement('style');
    style.id = 'prism-emr-pointer-styles';
    style.textContent = `
      @keyframes emrPointerBounce {
        0% { transform: translate(-50%, -20px) scale(0.8); opacity: 0; }
        50% { transform: translate(-50%, 5px) scale(1.05); }
        100% { transform: translate(-50%, 0) scale(1); opacity: 1; }
      }
      
      @keyframes emrPointerArrowPulse {
        0%, 100% { opacity: 0.9; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.2); }
      }
      
      .prism-emr-pointer-highlight {
        animation: emrPointerTargetPulse 1.5s ease-in-out infinite !important;
      }
      
      @keyframes emrPointerTargetPulse {
        0%, 100% { 
          box-shadow: 0 0 0 4px rgba(101, 169, 196, 0.6), 0 0 20px rgba(101, 169, 196, 0.4) !important;
        }
        50% { 
          box-shadow: 0 0 0 8px rgba(101, 169, 196, 0.3), 0 0 30px rgba(101, 169, 196, 0.6) !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Point to an EMR element with ghost path
   * @param {string|Element} elementSelector - CSS selector, text content, or DOM element
   * @param {string} title - Tooltip title
   * @param {string} message - Tooltip message (can include HTML)
   * @param {Object} options - { showButton, detectClick, onClose, position, autoDismiss }
   */
  pointTo(elementSelector, title, message, options = {}) {
    this.clear();
    
    let target;
    if (typeof elementSelector === 'string') {
      target = this.findElement(elementSelector);
    } else if (elementSelector instanceof Element) {
      target = elementSelector;
    } else {
      return false;
    }
    
    if (!target) {
      console.warn('[EMRPointer] Element not found:', elementSelector);
      return false;
    }
    
    // Create tooltip
    this.activeTooltip = this.createTooltip(title, message, options);
    document.body.appendChild(this.activeTooltip);
    
    // Highlight target
    this.highlightTarget(target);
    this.activeTarget = target;
    
    // Setup click detection if enabled
    if (options.detectClick && options.onClose) {
      this.setupClickDetection(target, options.onClose);
    }
    
    // Draw ghost path after brief delay
    setTimeout(() => {
      this.activePath = this.drawGhostPath(this.activeTooltip, target);
      if (this.activePath) {
        document.body.appendChild(this.activePath);
      }
    }, 100);
    
    // Auto-dismiss if specified
    if (options.autoDismiss) {
      setTimeout(() => {
        this.clear();
        if (options.onClose) options.onClose();
      }, options.autoDismiss);
    }
    
    return true;
  }

  findElement(selector) {
    // Try direct CSS selector first
    let element = document.querySelector(selector);
    if (element) return element;
    
    if (!selector) return null;
    
    // For "Add X" selectors, search buttons specifically
    if (selector.toLowerCase().includes('add ') || selector.toLowerCase().startsWith('add')) {
      const allButtons = document.querySelectorAll('button');
      
      // Exact aria-label match
      for (const btn of allButtons) {
        const ariaLabel = btn.getAttribute('aria-label');
        if (ariaLabel?.toLowerCase() === selector.toLowerCase()) {
          return btn;
        }
      }
      
      // Partial aria-label match
      for (const btn of allButtons) {
        const ariaLabel = btn.getAttribute('aria-label');
        if (ariaLabel?.toLowerCase().includes(selector.toLowerCase())) {
          return btn;
        }
      }
      
      // Button text content
      for (const btn of allButtons) {
        const btnText = btn.textContent?.trim().toLowerCase();
        if (btnText === selector.toLowerCase() || 
            btnText === '+ ' + selector.toLowerCase() ||
            btnText.includes(selector.toLowerCase())) {
          return btn;
        }
      }
    }
    
    // Search by aria-label
    const byAriaLabel = document.querySelector(`[aria-label="${selector}"]`);
    if (byAriaLabel) return byAriaLabel;
    
    // Case-insensitive aria-label search
    const allElements = document.querySelectorAll('[aria-label]');
    for (const el of allElements) {
      const ariaLabel = el.getAttribute('aria-label');
      if (ariaLabel?.toLowerCase() === selector.toLowerCase()) {
        return el;
      }
    }
    
    // Text content search
    if (!selector.startsWith('#') && !selector.startsWith('.') && !selector.startsWith('[')) {
      const allTextElements = document.querySelectorAll('*');
      let bestMatch = null;
      
      for (const el of allTextElements) {
        // Skip demo UI components
        if (el.closest('#prism-demo-control-panel') || el.closest('#prism-status-bar')) {
          continue;
        }
        
        // Prioritize elements in modals
        const inModal = el.closest('[role="dialog"]') || 
                       el.closest('.MuiDialog-root') ||
                       el.closest('.MuiModal-root');
        
        // Check direct text content
        const directText = Array.from(el.childNodes)
          .filter(node => node.nodeType === Node.TEXT_NODE)
          .map(node => node.textContent.trim())
          .join(' ');
        
        if (directText.toLowerCase().includes(selector.toLowerCase())) {
          if (!bestMatch || (inModal && !bestMatch.inModal)) {
            bestMatch = { element: el, inModal };
          }
        }
      }
      
      return bestMatch?.element || null;
    }
    
    return null;
  }

  createTooltip(title, message, options = {}) {
    const tooltip = document.createElement('div');
    tooltip.className = 'prism-emr-pointer-tooltip';
    
    const position = options.position || 'top-center';
    let positioning = '';
    
    switch (position) {
      case 'top-center':
        positioning = 'top: 100px; left: 50%; transform: translateX(-50%);';
        break;
      case 'top-left':
        positioning = 'top: 100px; left: 20px;';
        break;
      case 'top-right':
        positioning = 'top: 100px; right: 20px;';
        break;
      default:
        positioning = 'top: 100px; left: 50%; transform: translateX(-50%);';
    }
    
    tooltip.style.cssText = `
      position: fixed;
      ${positioning}
      max-width: 400px;
      background: linear-gradient(135deg, rgba(101, 169, 196, 0.95) 0%, rgba(91, 158, 130, 0.95) 100%);
      border-radius: 12px;
      padding: 20px;
      box-shadow: 0 10px 40px rgba(0,0,0,0.4);
      z-index: 2147483647;
      animation: emrPointerBounce 0.4s ease-out;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    `;
    
    tooltip.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
        <span style="font-size: 20px;">📍</span>
        <h3 style="margin: 0; color: white; font-size: 16px; font-weight: 600;">
          ${this.escapeHTML(title)}
        </h3>
      </div>
      <div style="margin: 0 0 ${options.showButton ? '16px' : '0'} 0; color: rgba(255,255,255,0.9); font-size: 13px; line-height: 1.5;">
        ${message}
      </div>
      ${options.showButton ? `
        <button class="prism-emr-pointer-close" style="
          background: white;
          color: rgba(91, 158, 130, 1);
          border: none;
          border-radius: 6px;
          padding: 10px 16px;
          font-weight: 600;
          cursor: pointer;
          font-size: 13px;
          width: 100%;
          transition: all 0.2s;
        " onmouseover="this.style.transform='scale(1.02)'" onmouseout="this.style.transform='scale(1)'">
          Got it!
        </button>
      ` : ''}
    `;
    
    // Add close button handler
    const closeBtn = tooltip.querySelector('.prism-emr-pointer-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.clear();
        if (options.onClose) options.onClose();
      });
    }
    
    return tooltip;
  }

  highlightTarget(element) {
    const computedStyle = window.getComputedStyle(element);
    const currentPosition = computedStyle.position;
    
    // Only set relative if it's not already positioned
    if (currentPosition !== 'fixed' && currentPosition !== 'absolute') {
      element.style.position = 'relative';
    }
    
    element.style.zIndex = '2147483645';
    element.style.boxShadow = '0 0 0 4px rgba(101, 169, 196, 0.6), 0 0 20px rgba(101, 169, 196, 0.4)';
    element.classList.add('prism-emr-pointer-highlight');
  }

  drawGhostPath(tooltipElement, targetElement) {
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const targetRect = targetElement.getBoundingClientRect();
    
    const startX = tooltipRect.left + tooltipRect.width / 2;
    const startY = tooltipRect.bottom;
    const endX = targetRect.left + targetRect.width / 2;
    const endY = targetRect.top + targetRect.height / 2;
    
    // Create curved path
    const xDiff = Math.abs(endX - startX);
    let controlX, controlY;
    
    if (xDiff < 100) {
      controlX = startX - 200;
      controlY = startY + (endY - startY) * 0.5;
    } else {
      controlX = startX + (endX - startX) * 0.5;
      controlY = Math.min(startY, endY) - 50;
    }
    
    const pathData = `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
    
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'prism-emr-pointer-svg');
    svg.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      pointer-events: none;
      z-index: 2147483646;
    `;
    
    // Path
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', pathData);
    path.setAttribute('stroke', 'rgba(101, 169, 196, 0.8)');
    path.setAttribute('stroke-width', '3');
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke-dasharray', '10 5');
    path.setAttribute('stroke-linecap', 'round');
    
    // Arrow
    const arrowSize = 12;
    const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    const angle = Math.atan2(endY - controlY, endX - controlX);
    const arrowPoints = [
      [endX, endY],
      [endX - arrowSize * Math.cos(angle - Math.PI / 6), endY - arrowSize * Math.sin(angle - Math.PI / 6)],
      [endX - arrowSize * Math.cos(angle + Math.PI / 6), endY - arrowSize * Math.sin(angle + Math.PI / 6)]
    ];
    arrow.setAttribute('points', arrowPoints.map(p => p.join(',')).join(' '));
    arrow.setAttribute('fill', 'rgba(101, 169, 196, 0.9)');
    arrow.style.animation = 'emrPointerArrowPulse 1.5s ease-in-out infinite';
    
    svg.appendChild(path);
    svg.appendChild(arrow);
    
    return svg;
  }

  setupClickDetection(target, onClose) {
    const clickHandler = (event) => {
      this.clear();
      setTimeout(() => {
        if (onClose) onClose();
      }, 600);
    };
    
    this.activeClickHandler = clickHandler;
    this.activeClickTarget = target;
    target.addEventListener('click', clickHandler, { once: true, capture: true });
  }

  clear() {
    if (this.activeClickTarget && this.activeClickHandler) {
      this.activeClickTarget.removeEventListener('click', this.activeClickHandler, { capture: true });
      this.activeClickHandler = null;
      this.activeClickTarget = null;
    }
    
    if (this.activePath) {
      this.activePath.remove();
      this.activePath = null;
    }
    
    if (this.activeTooltip) {
      this.activeTooltip.remove();
      this.activeTooltip = null;
    }
    
    if (this.activeTarget) {
      this.activeTarget.style.position = '';
      this.activeTarget.style.zIndex = '';
      this.activeTarget.style.boxShadow = '';
      this.activeTarget.classList.remove('prism-emr-pointer-highlight');
      this.activeTarget = null;
    }
  }

  escapeHTML(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Create singleton instance
const emrPointer = new EMRPointer();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.prismEMRPointer = emrPointer;
}

export default emrPointer;
